<template>
  <div>
    <main class="text-dark-teal-600 z-[1] relative bg-white min-h-screen pt-12">
      <!-- Hero Section -->
      <header class="relative flex flex-col items-center justify-center h-auto bg-green-200 text-center gap-20 px-8 pt-16 pb-20 md:pt-20 md:pb-24 lg:pt-28 lg:pb-36">
        <div class="flex flex-col max-w-screen-sm gap-8 lg:gap-10 lg:max-w-screen-md">
          <div class="text-xl md:text-2xl">{{ t('pages.pricing.hero.kicker') }}</div>
          <h1 class="text-5xl font-medium md:text-6xl md:leading-[0.9] lg:text-8xl">{{ t('pages.pricing.hero.title') }}</h1>
          <div class="text-xl md:text-2xl">{{ t('pages.pricing.hero.subtitle') }}</div>
        </div>
        
        <!-- Decorative Coins -->
        <div class="pointer-events-none absolute inset-0 overflow-visible parallax-up">
          <ParallaxCoin
            src="https://assets.gumroad.com/assets/pricing-coin-4-84a5d2209e88d26e9478c1c3ec751d7cb4c14262f9bd244b46892f877a581523.svg"
            :alt="t('pages.pricing.hero.coins.alt1')"
            className="opacity-100 left-[-2rem] top-[12rem] z-[1] w-[6.875rem] lg:left-auto lg:right-[10vw] lg:top-[22rem]"
            :scrollIntensity="-0.15"
            :mouseIntensity="0.03"
          />
          <ParallaxCoin
            src="https://assets.gumroad.com/assets/pricing-coin-5-bc80d31182b7c33922a1d0f4a41d3a0a5e329544812e5b34602dfe3006e43c1b.svg"
            :alt="t('pages.pricing.hero.coins.alt2')"
            className="opacity-50 right-[1rem] top-[6rem] z-[1] w-[12rem] lg:right-[2rem] lg:bottom-[-4rem] lg:top-auto blur-sm"
            :scrollIntensity="-0.18"
            :mouseIntensity="-0.02"
          />
          <ParallaxCoin
            src="https://assets.gumroad.com/assets/pricing-coin-6-a7523d8f4a39f9c2b446408ade5a46eb3bbec36c11c3c4bbf970bd45046c832d.svg"
            :alt="t('pages.pricing.hero.coins.alt3')"
            className="opacity-20 bottom-[-14rem] sm:bottom-[-12rem] left-1/2 z-[1] w-[6rem] -translate-x-1/2 md:bottom-[-16rem] md:w-[9rem] lg:bottom-[-24rem] lg:w-[11rem]"
            :scrollIntensity="-0.16"
            :mouseIntensity="0.04"
          />
          <ParallaxCoin
            src="https://assets.gumroad.com/assets/pricing-coin-2-0c8ae69e416f1d3848fa34ddab47269acee56d826f5756bcfd2a3eccec512791.svg"
            :alt="t('pages.pricing.hero.coins.alt4')"
            className="opacity-60 invisible absolute left-[10vw] top-[14rem] z-[1] w-[14rem] overflow-visible lg:visible blur-sm" 
            :scrollIntensity="-0.17"
            :mouseIntensity="-0.035"
          />
          <ParallaxCoin
            src="https://assets.gumroad.com/assets/pricing-coin-1-af4c3239fd799a1c1ab1e84d9ac4904e85bc54bfa2e60d87574f2fb742f39c69.svg"
            :alt="t('pages.pricing.hero.coins.alt5')"
            className="opacity-40 invisible absolute left-[-1rem] top-[14rem] z-[1] w-[8rem] overflow-hidden lg:visible"
            :scrollIntensity="-0.15"
            :mouseIntensity="0.03"
          />
          <ParallaxCoin
            src="https://assets.gumroad.com/assets/pricing-coin-3-454820ffae345ee798df5ee47a2e0892d9cde2a94cea577023d776b9ce6a0b0b.svg"
            :alt="t('pages.pricing.hero.coins.alt6')"
            className="opacity-60 invisible absolute bottom-[-6rem] left-[-2rem] z-[1] w-[14rem] overflow-hidden lg:visible"
            :scrollIntensity="-0.16"
            :mouseIntensity="-0.02"
          />
        </div>
      </header>

      <!-- Early Bird Section -->
      <div class="bg-light-brown-100 py-16 px-8 md:py-24 md:px-16">
        <div class="max-w-5xl mx-auto space-y-8">
          <div class="text-center space-y-4">
            <h2 class="text-4xl md:text-5xl font-medium text-dark-teal-600">{{ t('pages.pricing.earlyBird.title') }}</h2>
            <p class="text-xl md:text-2xl text-dark-teal-600">{{ t('pages.pricing.earlyBird.description') }}</p>
            <p class="text-lg text-dark-teal-600/80">{{ t('pages.pricing.earlyBird.note') }}</p>
          </div>
          
          <!-- Early Bird Tiers Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            <div 
              v-for="(tier, index) in earlyBirdTiers" 
              :key="index"
              :class="[
                'rounded-lg border-2 bg-white p-6 space-y-4 transition-colors',
                getTierStatus(index) === 'active' 
                  ? 'border-coral-600 border-4' 
                  : getTierStatus(index) === 'past'
                  ? 'border-green-200 opacity-30'
                  : 'border-green-200',
                // Center the last item when it's alone on large screens (5 items = odd)
                index === earlyBirdTiers.length - 1 && earlyBirdTiers.length % 4 === 1
                  ? 'lg:col-start-2 lg:col-span-2'
                  : ''
              ]"
            >
              <div class="space-y-2">
                <div class="text-sm font-medium text-dark-teal-600/70 uppercase tracking-wide">{{ tier.date }}</div>
                <div class="text-3xl md:text-4xl font-medium text-dark-teal-600">{{ tier.discount }}</div>
              </div>
              <div class="space-y-2 pt-4 border-t border-dark-teal-600/20">
                <div class="text-sm text-dark-teal-600">
                  <span class="font-medium">Collection:</span><br />{{ tier.collectionExample }}
                </div>
                <div class="text-sm text-dark-teal-600">
                  <span class="font-medium">Marketplace:</span><br />{{ tier.marketplaceExample }}
                </div>
              </div>
            </div>
          </div>
          
          <!-- Apply CTA Button -->
          <div class="flex flex-col items-center mt-12 space-y-3">
            <div class="relative inline-block group">
              <div class="absolute inset-0 rounded bg-yellow-500 border border-black transition-transform duration-150 group-hover:-translate-x-4 z-20"></div>
              <div class="absolute inset-0 rounded bg-yellow-200 border border-black transition-transform duration-150 group-hover:-translate-x-6 group-hover:translate-y-2 z-10"></div>
              <NuxtLink 
                class="relative inline-flex rounded no-underline items-center justify-center border border-black transition-all duration-150 group-hover:-translate-x-2 group-hover:-translate-y-2 z-30 w-full lg:w-auto h-14 px-8 text-xl lg:h-16 lg:px-10 lg:text-xl bg-dark-teal-600 text-white" 
                :to="localePath('join')"
              >
                {{ t('pages.pricing.earlyBird.applyCta') }}
              </NuxtLink>
            </div>
            <p class="text-sm text-dark-teal-600/70 text-center">{{ t('pages.pricing.earlyBird.discountForever') }}</p>
          </div>
        </div>
      </div>

      <!-- Pricing Tiers -->
      <div class="flex flex-col overflow-hidden lg:flex-row">
        <div class="bg-teal-100 flex items-center justify-center text-center lg:w-1/2 px-4 py-16 md:p-24 xl:p-32 border-b lg:border-b-0 lg:border-r">
          <div class="max-w-xl space-y-12 md:space-y-8">
            <div class="space-y-8">
              <h3 class="font-medium text-5xl lg:text-6xl">
                {{ t('pages.pricing.tiers.collection.headline') }}
              </h3>
              <p class="text-xl lg:text-2xl">
                {{ t('pages.pricing.tiers.collection.body') }}
              </p>
            </div>
          </div>
        </div>
        <div class="bg-teal-200 flex items-center justify-center text-center lg:w-1/2 px-4 py-16 md:p-16 xl:p-32">
          <div class="max-w-xl space-y-12 md:space-y-8">
            <div class="space-y-8">
              <h3 class="font-medium text-5xl lg:text-6xl">
                {{ t('pages.pricing.tiers.marketplace.headline') }}
              </h3>
              <p class="text-xl lg:text-2xl">
                {{ t('pages.pricing.tiers.marketplace.body') }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <!-- Benefits Section -->
      <div class="flex flex-col border-b border-black overflow-hidden lg:flex-row">
        <div class="bg-coral-300 h-auto lg:w-1/2 border-b lg:border-b-0">
          <div class="relative">
            <SmartImage class="h-auto w-full" :alt="t('pages.pricing.benefits.imageAlt')" src="/images/great-currency.svg" />
          </div>
        </div>
        <div class="bg-black text-white py-16 px-8 flex items-center justify-center sm:p-12 md:p-16 lg:w-1/2 xl:p-24">
          <div class="max-w-2xl space-y-12 md:space-y-8">
            <div class="space-y-4">
              <h3 class="font-medium text-3xl lg:text-4xl xl:text-5xl text-coral-300">
                {{ t('pages.pricing.benefits.title') }}
              </h3>
              <p class="text-lg lg:text-xl xl:text-2xl text-coral-300">
                {{ t('pages.pricing.benefits.description') }}
              </p>
            </div>
            <ul class="grid grid-cols-1 gap-3 pl-0 text-lg md:grid-cols-2">
              <li v-for="bullet in benefitsBullets" :key="bullet" class="flex items-center">
                <span class="mr-2">→</span>
                {{ bullet }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- FAQ Section -->
      <div class="mx-auto my-16 max-w-6xl px-8 md:my-24 md:px-28 md:text-center lg:my-32">
        <h2 class="text-5xl font-medium md:text-6xl lg:text-7xl">
          {{ t('pages.pricing.faq.title') }}
        </h2>
      </div>

      <div class="mx-auto mb-16 px-8 md:px-28 border-b-2 border-black">
        <div class="accordion-container">
          <div v-for="(item, index) in faqItems" :key="index" class="accordion-item">
            <div 
              class="flex justify-between items-center py-6 sm:py-12 md:py-16 border-t border-black cursor-pointer" 
              @click="toggleFaq(index)"
            >
              <div class="flex items-center w-full gap-[4vw] md:gap-[12vw]">
                <div class="w-12 h-12 flex-shrink-0 flex items-center justify-center">
                  <svg width="48" height="48" viewBox="0 0 32 32">
                    <line x1="8" y1="16" x2="24" y2="16" stroke="black" stroke-width="2"/>
                    <line 
                      x1="16" 
                      y1="8" 
                      x2="16" 
                      y2="24" 
                      stroke="black" 
                      stroke-width="2" 
                      :class="{'rotate-90': openFaq === index}"
                      class="transition-transform duration-300"
                    />
                  </svg>
                </div>
                <h3 class="text-2xl md:text-4xl font-medium">{{ item.question }}</h3>
              </div>
            </div>

            <div 
              class="overflow-hidden transition-all duration-300"
              :class="{'max-h-[1000px]': openFaq === index, 'max-h-0': openFaq !== index}"
            >
              <div class="py-8">
                <p class="text-xl md:text-2xl" v-html="item.answer"></p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Share Your Work Section -->
      <!-- <div class="flex flex-col items-center justify-center text-center border-t border-black bg-coral-400 gap-8 px-8 py-16 lg:px-[4vw] lg:py-24 lg:gap-16">
        <h2 class="text-4xl font-medium sm:text-5xl lg:text-7xl">
          Share your work. <br> Someone out there needs it.
        </h2>
        
        <div class="relative inline-block group">
          <div class="absolute inset-0 rounded bg-light-brown border border-black transition-transform duration-150 z-20"></div>
          <div class="absolute inset-0 rounded bg-coral-400 border border-black transition-transform duration-150 group-hover:translate-x-2 group-hover:translate-y-2 z-10"></div>
          <a class="relative inline-flex rounded no-underline items-center justify-center border border-black transition-all duration-150 group-hover:-translate-x-2 group-hover:-translate-y-2 z-30 w-full lg:w-auto h-14 px-8 text-xl lg:h-16 lg:px-10 lg:text-xl bg-light-brown-400 text-black" href="/users/sign_up">
            Start selling
          </a>
        </div>
      </div> -->
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n, useLocalePath } from '#imports'
import { useSeo, buildFaqPageSchema } from '~/composables/useSeo'
import ParallaxCoin from '../components/ParallaxCoin.vue'
import SmartImage from '~/components/SmartImage.vue'

definePageMeta({
  layout: 'default'
})

const { t, tm } = useI18n()
const localePath = useLocalePath()

type FaqItem = {
  question: string
  answer: string
}

const faqItems = computed(() => (tm('pages.pricing.faq.items') as FaqItem[]) ?? [])

useSeo({
  title: t('pages.pricing.meta.title'),
  description: t('pages.pricing.meta.description'),
  keywords: 'ommi.shop pricing, marketplace fees, seller fees, transaction fees, transparent pricing, no hidden costs',
  type: 'website',
  structuredData: faqItems.value.length > 0 ? buildFaqPageSchema(faqItems.value) : undefined
})

const openFaq = ref<number | null>(null)
const benefitsBullets = computed(() => (tm('pages.pricing.benefits.bullets') as string[]) ?? [])
const earlyBirdTiers = computed(() => (tm('pages.pricing.earlyBird.tiers') as Array<{ date: string; discount: string; collectionExample: string; marketplaceExample: string }>) ?? [])

// Tier date thresholds (end dates for each discount period)
const tierEndDates = [
  new Date('2025-11-15'), // Before Nov 15, 2025: 70%
  new Date('2025-12-15'), // Before Dec 15, 2025: 50%
  new Date('2026-01-15'), // Before Jan 15, 2026: 25%
  new Date('2026-02-15'), // Before Feb 15, 2026: 10%
  new Date('2026-03-15'), // From Mar 15, 2026: Full fees
]

// Helper function to compare dates ignoring time (only day/month/year)
const compareDatesOnly = (date1: Date, date2: Date): number => {
  const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate())
  const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate())
  if (d1 < d2) return -1
  if (d1 > d2) return 1
  return 0
}

// Determine tier status: 'past', 'active', or 'future'
const getTierStatus = (index: number): 'past' | 'active' | 'future' => {
  const now = new Date()
  const tierEndDate = tierEndDates[index]
  const previousTierEndDate = index > 0 ? tierEndDates[index - 1] : null
  
  // For the first tier (70% discount), it's active from the start until Nov 15, 2025 (inclusive)
  if (index === 0) {
    if (compareDatesOnly(now, tierEndDate) <= 0) {
      return 'active'
    }
    return 'past'
  }
  
  // For the last tier (full fees), it's active from Mar 15, 2026 onwards (inclusive)
  if (index === tierEndDates.length - 1) {
    if (compareDatesOnly(now, tierEndDate) >= 0) {
      return 'active'
    }
    return 'future'
  }
  
  // For middle tiers, they're active between the day after previous tier's end and their own end date (inclusive)
  // Previous tier ends on the 15th, so next tier starts on the 16th
  if (previousTierEndDate) {
    const dayAfterPrevious = new Date(previousTierEndDate)
    dayAfterPrevious.setDate(dayAfterPrevious.getDate() + 1)
    
    if (compareDatesOnly(now, dayAfterPrevious) >= 0 && compareDatesOnly(now, tierEndDate) <= 0) {
      return 'active'
    }
  }
  
  // Past if we've passed this tier's end date
  if (compareDatesOnly(now, tierEndDate) > 0) {
    return 'past'
  }
  
  return 'future'
}

const toggleFaq = (index: number): void => {
  openFaq.value = openFaq.value === index ? null : index
}
</script>

<style scoped>
.parallax-up {
  perspective: 1000px;
}

.parallax-up img {
  transition: transform 0.1s ease-out;
  will-change: transform;
}

a, button {
  transition: all 0.2s ease-in-out;
}

a:hover, button:hover {
  transform: translateY(-2px);
}

a:focus, button:focus {
  outline: 2px solid #000;
  outline-offset: 2px;
}

@media (prefers-color-scheme: dark) {
  .bg-\[\#FFE4D4\] {
    background-color: #ffad69ff;
  }
  .bg-\[\#E4D4FF\] {
    background-color: #1e3888ff;
  }
  .bg-\[\#D4FFE4\] {
    background-color: #c2e2e3ff;
  }
  .bg-\[\#FFD4E4\] {
    background-color: #9c3848ff;
  }
}

.accordion-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.accordion-item {
  border-top: 1px solid black;
}

.accordion-item:first-child {
  border-top: none;
}
</style> 